/**
 * UsersPanel — uživatelé GitLab instance (gitlab.user_manage).
 *
 * Filip 2026-09-05 (převzatý GitLab CIE): „potřebuju, aby gitlab uměl z UI
 * uživatele, čistění, updaty." Účty se dosud daly řešit jen MCP toolem;
 * v dashboardu chyběl seznam i blokace/mazání. Boti a servisní účty z dřívějších
 * pokusů se tak dají projít a uklidit odsud.
 *
 * Widget-first: obsah bez vnějšího Card (chrome kreslí WidgetShell). Vzor =
 * RunnersPanel (tabulka + akce + potvrzení).
 */
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, CheckCircle2, ChevronDown, ChevronRight, KeyRound, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Input, StatusDot } from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { IdentityPairing } from './IdentityPairing';
import { TokensBlock } from './TokensBlock';

interface GitlabUser {
  id: number;
  username: string;
  name?: string;
  email?: string;
  state?: string;
  is_admin?: boolean;
  bot?: boolean;
  external?: boolean;
  last_activity_on?: string | null;
  last_sign_in_at?: string | null;
  created_at?: string | null;
}
interface UsersResp {
  ok: boolean;
  host?: string;
  count?: number;
  users?: GitlabUser[];
  error?: string;
}
interface ConnectionsResp {
  ok: boolean;
  servers?: Array<{ id: string; host: string; has_token: boolean; kind?: string; instance_name?: string | null }>;
}

function nodeKey(scope: EffectiveScope): string {
  return scope.node ?? 'local';
}

function stateDot(u: GitlabUser): 'running' | 'stopped' | 'error' | 'pending' {
  if (u.state === 'active') return 'running';
  if (u.state === 'blocked' || u.state === 'banned') return 'error';
  if (u.state === 'deactivated' || u.state === 'blocked_pending_approval') return 'pending';
  return 'stopped';
}

export function UsersPanel({ scope }: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sc: EffectiveScope = scope ?? { node: null, identity: null };
  const nk = nodeKey(sc);
  const [connection, setConnection] = useState<string>('');
  const [filter, setFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', name: '', password: '', admin: false });
  const [openUserId, setOpenUserId] = useState<number | null>(null);

  const conns = useQuery<ConnectionsResp>({
    queryKey: ['gitlab-connections', nk],
    queryFn: () => callWidgetTool<ConnectionsResp>('gitlab.connections_list', {}, sc, { silent: true }),
    staleTime: 60_000,
  });
  const servers = conns.data?.servers ?? [];
  const effectiveConn = connection || (servers.length === 1 ? servers[0]?.id : '') || '';

  const q = useQuery<UsersResp>({
    queryKey: ['gitlab-users', nk, effectiveConn],
    queryFn: () =>
      callWidgetTool<UsersResp>(
        'gitlab.user_manage',
        { action: 'list', ...(effectiveConn ? { connection_id: effectiveConn } : {}) },
        sc,
        { silent: true },
      ),
    enabled: conns.isSuccess && !!effectiveConn,
    staleTime: 15_000,
  });

  const users = useMemo(() => {
    const all = q.data?.users ?? [];
    const f = filter.trim().toLowerCase();
    const list = f
      ? all.filter((u) =>
        [u.username, u.name, u.email].some((v) => (v ?? '').toLowerCase().includes(f))
      )
      : all;
    return [...list].sort((a, b) => a.username.localeCompare(b.username));
  }, [q.data, filter]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['gitlab-users', nk, effectiveConn] });

  const act = useMutation({
    mutationFn: (args: { user: GitlabUser; action: 'block' | 'unblock' | 'delete'; hard?: boolean }) =>
      callWidgetTool<{ ok?: boolean; error?: string }>(
        'gitlab.user_manage',
        {
          action: args.action,
          user_id: args.user.id,
          ...(effectiveConn ? { connection_id: effectiveConn } : {}),
          ...(args.action === 'delete' ? { hard_delete: args.hard === true } : {}),
        },
        sc,
        { label: t('gitlab.users.actionLabel', { action: args.action, name: args.user.username }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      invalidate();
    },
  });

  const createMut = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; error?: string; username?: string }>(
        'gitlab.user_manage',
        {
          action: 'create',
          username: newUser.username.trim(),
          email: newUser.email.trim(),
          name: newUser.name.trim(),
          ...(newUser.password ? { password: newUser.password } : {}),
          admin: newUser.admin,
          ...(effectiveConn ? { connection_id: effectiveConn } : {}),
        },
        sc,
        { label: t('gitlab.users.createLabel', { name: newUser.username }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      setNewUser({ username: '', email: '', name: '', password: '', admin: false });
      setCreateOpen(false);
      invalidate();
    },
  });
  const canCreate = !!(newUser.username.trim() && newUser.email.trim() && newUser.name.trim());

  const onDelete = async (u: GitlabUser) => {
    const ok = await themedConfirm({
      title: t('gitlab.users.deleteTitle', { name: u.username }),
      description: t('gitlab.users.deleteDesc'),
      confirmLabel: t('gitlab.users.deleteConfirm'),
    });
    if (!ok) return;
    act.mutate({ user: u, action: 'delete' });
  };

  const admins = users.filter((u) => u.is_admin).length;
  const bots = users.filter((u) => u.bot).length;

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-zinc-400">
          {t('gitlab.users.title', { count: q.data?.users?.length ?? 0, admins, bots })}
        </span>
        <div className="flex items-center gap-2">
          {servers.length > 1 && (
            <Select
              value={effectiveConn}
              onChange={(e) => setConnection((e.target as HTMLSelectElement).value)}
              className="text-xs"
            >
              <option value="">{t('gitlab.selectConnection', 'Vyber GitLab')}</option>
              {servers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} · {s.host.replace(/^https?:\/\//, '')}
                </option>
              ))}
            </Select>
          )}
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('gitlab.users.filterPlaceholder')}
            className="text-xs w-48"
          />
          <Button variant="secondary" size="sm" onClick={() => setCreateOpen((v) => !v)}>
            <Plus size={13} /> {t('gitlab.users.new')}
          </Button>

        </div>
      </div>

      {effectiveConn && <IdentityPairing connection={effectiveConn} scope={sc} />}

      {createOpen && (
        <div className="rounded-lg border border-zinc-800 p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              value={newUser.username}
              onChange={(e) => setNewUser((u) => ({ ...u, username: e.target.value }))}
              placeholder={t('gitlab.users.usernamePlaceholder')}
            />
            <Input
              value={newUser.email}
              onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
              placeholder={t('gitlab.users.emailPlaceholder')}
            />
            <Input
              value={newUser.name}
              onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))}
              placeholder={t('gitlab.users.namePlaceholder')}
            />
            <Input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
              placeholder={t('gitlab.users.passwordPlaceholder')}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <input
              type="checkbox"
              checked={newUser.admin}
              onChange={(e) => setNewUser((u) => ({ ...u, admin: e.target.checked }))}
            />
            {t('gitlab.users.createAdmin')}
          </label>
          <Button
            variant="primary"
            size="sm"
            loading={createMut.isPending}
            disabled={!canCreate}
            onClick={() => createMut.mutate()}
          >
            {t('gitlab.users.create')}
          </Button>
        </div>
      )}

      {conns.isSuccess && servers.length === 0
        ? (
          <EmptyState
            title={t('gitlab.users.noConnectionTitle')}
            description={t('gitlab.users.noConnectionDesc')}
          />
        )
        : q.isLoading || conns.isLoading
        ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
        : q.data?.ok === false
        ? <div className="text-sm text-red-400">{q.data.error ?? t('gitlab.users.loadFailed')}</div>
        : users.length === 0
        ? <EmptyState title={t('gitlab.users.emptyTitle')} description={t('gitlab.users.emptyDesc')} />
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                  <th className="text-left py-2 pr-3">{t('gitlab.users.colUser')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.users.colEmail')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.users.colRole')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.users.colActivity')}</th>
                  <th className="text-right py-2 pr-3">{t('gitlab.users.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const blocked = u.state === 'blocked' || u.state === 'banned';
                  const activity = u.last_activity_on ?? u.last_sign_in_at?.slice(0, 10) ?? null;
                  const open = openUserId === u.id;
                  return (
                    <Fragment key={u.id}>
                    <tr className="border-b border-zinc-900 hover:bg-zinc-900/30">
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          className="font-medium flex items-center gap-2 text-left"
                          onClick={() => setOpenUserId(open ? null : u.id)}
                        >
                          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          <StatusDot status={stateDot(u)} />
                          {u.username}
                        </button>
                        <div className="text-xs text-zinc-500 pl-6">
                          {u.name ?? ''}
                          {u.state && u.state !== 'active' ? ` · ${u.state}` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs text-zinc-400">
                        <div className="max-w-[260px] truncate" title={u.email ?? ''}>{u.email ?? '—'}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {u.is_admin && <Badge variant="purple">{t('gitlab.users.admin')}</Badge>}
                          {u.bot && <Badge variant="gray">{t('gitlab.users.bot')}</Badge>}
                          {u.external && <Badge variant="blue">{t('gitlab.users.external')}</Badge>}
                          {!u.is_admin && !u.bot && !u.external && (
                            <span className="text-xs text-zinc-600">{t('gitlab.users.regular')}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs text-zinc-400">
                        {activity ?? <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center justify-end gap-1">
                          {!u.bot && (
                            blocked
                              ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={act.isPending}
                                  onClick={() => act.mutate({ user: u, action: 'unblock' })}
                                >
                                  <CheckCircle2 size={13} /> {t('gitlab.users.unblock')}
                                </Button>
                              )
                              : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={act.isPending || u.username === 'root'}
                                  onClick={() => act.mutate({ user: u, action: 'block' })}
                                >
                                  <Ban size={13} /> {t('gitlab.users.block')}
                                </Button>
                              )
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenUserId(open ? null : u.id)}
                          >
                            <KeyRound size={13} /> {t('gitlab.tokens.open')}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={act.isPending || u.username === 'root' || u.bot}
                            onClick={() => void onDelete(u)}
                          >
                            <Trash2 size={13} /> {t('gitlab.users.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={5} className="pb-3 pt-0">
                          <TokensBlock
                            scope={sc}
                            connectionId={effectiveConn || undefined}
                            target="user"
                            targetId={u.id}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
